import moment from 'moment';
import {EVENT_TAB_TYPES, GROUP_EVENT_TAB_TYPES} from '../constants/EventTabs';
import {DOMAIN} from '../constants/URLs';
import {userLocation} from '../models/UserLocation';
import {userProfile} from '../models/UserProfile';
import {projectConfig} from '../project-info/projectConfig';
import {localizeDate} from './Helpers';

export function isCreateEventButtonVisible(
  groupName,
  activeTab,
  isLoading,
  createVisible,
  user,
  groupType,
  isAdmin,
  joinedGrouped,
  challengeId,
) {
  let isVisible = true;
  if (projectConfig.alwaysShowEventButton) {
    return isVisible;
  }
  // group event list
  if (groupName) {
    if (activeTab === GROUP_EVENT_TAB_TYPES.past) isVisible = false;
    if (!joinedGrouped) isVisible = false;
    groupType = groupType.toLowerCase();
    // chapter groups
    if (groupType === 'chapter') {
      // user is not an eagle leader, or lacks the admin membership role
      if (
        activeTab === GROUP_EVENT_TAB_TYPES.local &&
        !user.eagle_leader &&
        !isAdmin
      )
        isVisible = false;
      // user lacks the admin membership role
      else if (activeTab === GROUP_EVENT_TAB_TYPES.virtual && !isAdmin)
        isVisible = false;
    } else if (groupType === 'activity') {
      // user lacks the admin membership role
      if (!isAdmin) isVisible = false;
    } else if (groupType === 'sponsor') {
      // user lacks the admin membership role
      if (!isAdmin) isVisible = false;
    } else if (groupType === 'national') {
      if (!isAdmin) isVisible = false; // not sure if national should always be false
    }
  }
  // event tab events list
  else {
    if (activeTab === EVENT_TAB_TYPES.virtual && projectConfig.allowsVirtualEventsOutsideGroups === false) isVisible = false;
    if (projectConfig.limitedNonGroupEventCreation) {
      if (!user.eagle_leader && !isAdmin) isVisible = false;
    }
  }
  if (isLoading || createVisible || user.anonymous_profile || challengeId)
    isVisible = false;
  return isVisible;
}


// This is not a "localeFormatter", it's returnlastLocaleOrformatLocaleFromUserLocationOrProfileLocation
// It merely returns the last locale and city if passed to it (wut), and if there isn't, it pokes around in devices location or user loation for city, state, zip, and makes a locale from that.
// the eventURLFormatter prefers lat/long but falls back to locale if there's no lat/long and locale exists.
// I think the only real value of this is that it can format a locale from the user's location as stored on backend which doesn't include lat/long.
// it's possible we don't need that very much moving forward.


// 05/09 I've decided to deprecate localeFormatter and remove it. as of today, it's removed from the web app. Mobile is next
// nearly all places that would need locale wrote around this over time. eventURLFormatter has it's own naive way of doing it. 
// Event.js has extractLocale which looks simpler and better.
// on testing, removing locale from event.js state is working fine. We just don't need it there anymore.

export function localeFormatter(lastLocale, lastCity) {
  const DEFAULT_LOCALE = 'Everywhere';
  let locale = '';
  let displayCity = '';
  if (!lastLocale) {
    let {city, state, zip} = userLocation.getUserLocation(); // userLocation stores lat/long so unclear why this would be useful. Perhaps userlocation will not always have lat/long
    if (city || state || zip) {
      if (zip) {
        locale += `${zip.slice(0, 5)}`;
      }
      if (city) {
        locale += ` ${city}`;
      }
      if ((city && state) || (zip && state && !city)) {
        locale += `,`;
      }
      if (state) {
        locale += ` ${state}`;
      }
      if (city) displayCity = city;
    } else {
      const profile = userProfile.getUserProfile ? userProfile.getUserProfile() : {};
      const safeLocation = profile && profile.location ? profile.location : {};
      let {zip, city, address_state} = safeLocation; // get the location info from the profile as retrieved from the backend

      if (zip) {
        locale += `${zip.slice(0, 5)}`;
      }
      if (city) {
        locale += ` ${city}`;
      }
      if ((city && address_state) || (zip && address_state && !city)) {
        locale += `,`;
      }
      if (address_state) {
        locale += ` ${address_state}`;
      }
      if (city) displayCity = city;
      locale = locale.trim();
    }
    if (locale === '') {
      locale = DEFAULT_LOCALE;
    }
  } else {
    locale = lastLocale;
    displayCity = lastCity;
  }
  return {locale, displayCity};
}

export function getDisplayDate(utcStartDateTime) {
  // understanding if an event is today or tomorrow is a local time concept.
  // 2 hours from an event at midnight UTC is "tomorrow" for a UTC-lander.
  // at the same moment in time, that event is "today" for a PST-lander.
  // in other words, you can't rely on utc comparison for dates.
  
  const today = moment();
  const tomorrow = moment().add(1, 'days');

  if (moment(utcStartDateTime).local().isSame(today, 'day')) {
    return 'Today';
  } else if (moment(utcStartDateTime).local().isSame(tomorrow, 'day')) {
    return 'Tomorrow';
  } else {
    return moment(utcStartDateTime).local().format('ddd, MMM DD, YYYY');
  }
}

export function getAllDayEventTimes(date) {
  const startOfDay = moment(date)
    .startOf('day')
    .utc()
    .format('YYYY-MM-DD HH:mm:ss');
  const endOfDay = moment(date)
    .endOf('day')
    .utc()
    .format('YYYY-MM-DD HH:mm:ss');
  return {startOfDay, endOfDay};
}

// TODO: Remove this function and replace with the new utc-everywhere strategy.
// Leaving in for now because web app uses it.
export function isEventOver(eventData) {
  const today = moment();
  const eventDate = localizeDate(
    eventData.time_zone_id,
    eventData.end,
  );
  return today.isAfter(eventDate);
}

export function isVirtualEventJoinable(eventData) {
  if (!eventData || !eventData.utcStartDateTime || !eventData.utcEndDateTime) {
    return false;
  }
  const today = moment();
  
  const eventStartDate = eventData.utcStartDateTime;
  const eventEndDate = eventData.utcEndDateTime;

  // pad 10 minutes on either side. RSVP'd users get a notification 10 minutes before, so we are matching that here.
  // It seems better to display a "join zoom" button for users who respond to a "event is starting" noticication.
  // other improvements could be made (such as a countdown timer)
  const adjustedEventStartDate = eventStartDate.clone().subtract(10, 'minutes');
  const adjustedEventEndDate = eventEndDate.clone().add(10, 'minutes');

  return today.isBetween(adjustedEventStartDate, adjustedEventEndDate, null, '[]');
}

export function generateInitialNextURL(eventID, status) {
  // TODO: Improve this, backend returns more than 10 for each type but front end only passes 10.
  return `${DOMAIN}${
    projectConfig.apiVersion
  }event/${eventID}/attendee?limit=10&offset=10&status=${status.slice(0, 1)}`;
}
